import React, { Dispatch, FC, useCallback, useMemo, useState } from 'react';
import axios from 'axios';

import { Popover } from '@headlessui/react';
import moment from 'moment';
import {
  HeaderGroup,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import ReactTooltip from 'react-tooltip';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import { ReactComponent as RemovedInfo } from 'assets/icons/removed-info.svg';
import RebookingWarningMessage from 'components/RebookingWarningMessage/RebookingWarningMessage';

import IndeterminateCheckbox from './IndeterminateCheckbox/IndeterminateCheckbox';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import axiosInstance from 'apis/axiosInstance';

import { STATUS, ERROR_REASON } from 'utils/constants';
import ErrorIcon from 'components/ErrorIcon/ErrorIcon';
import RebookingPopover from 'components/RebookingPopover/RebookingPopover';
import ThreeDotsOption from './ThreeDotsOption/ThreeDotsOption';

import styles from './Table.module.css';
import AppointmentDetailModal from 'components/AppointmentDetailModal/AppointmentDetailModal';
import PractitionerNameColumn from 'components/TableColumn/PractitionerNameColumn/PractitionerNameColumn';
import AppointmentDateTimeColumn from 'components/TableColumn/AppointmentDateTimeColumn/AppointmentDateTimeColumn';
import {
  getErrorReason,
  getIsWarningIconEnabled,
  isWarningIconExistedAfter24Hour,
} from 'utils/common';
import PopoverOption from 'components/PopoverOption/PopoverOption';
import { renderToast } from 'components/Toast/Toast';
import AcceptAppointmentModal from 'components/AcceptAppointmentModal/AcceptAppointmentModal';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import RejectAppointmentModal from 'components/RejectAppointmentModal/RejectAppointmentModal';
import KeepBothAppointmentsModal from 'components/KeepBothAppointmentsModal/KeepBothAppointmentsModal';
import { IAppointmentDetail } from 'interfaces/appointments';

interface TableProps {
  currentPage: number;
  setCurrentPage: Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: Dispatch<React.SetStateAction<number>>;
  sortBy: string;
  setSortBy: Dispatch<React.SetStateAction<string>>;
  sort: 'asc' | 'desc';
  setSort: Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  status: 'new' | 'accepted';
  data: any;
  isLoading: boolean;
  updateDashboardUI: () => Promise<void>;
}

const Table: FC<TableProps> = ({
  sort,
  sortBy,
  setCurrentPage,
  setPageSize,
  setSort,
  setSortBy,
  status,
  data,
  isLoading,
  updateDashboardUI,
}) => {
  // TODO: change to useReducer in the future

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalAccepted, setIsModalAccepted] = useState(false);
  const [chosenAppointmentId, setChosenAppointmentId] = useState('');

  const [isAllSelected, setIsAllSelected] = useState(false);

  const [highlightedRowId, setHighlighedRowId] = useState('');

  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');

  const [isRetrySelected, setIsRetrySelected] = useState(false);

  const [isKeepBothApptModalOpen, setIsKeepBothApptModalOpen] = useState(false);
  const [selectedApptForKeepBothModal, setSelectedApptForKeepBothModal] =
    useState<IAppointmentDetail | null>(null);

  const onModalClose = useCallback(() => setIsModalOpen(false), []);

  const options = useMemo(
    () => (status === 'new' ? [STATUS.ACCEPT, STATUS.REJECT] : []),
    [status]
  );

  const headerOptions = useMemo(
    () => (status === 'new' ? [STATUS.ACCEPT] : []),
    [status]
  );

  const isActionColumn = (columnId: string) =>
    ['selection', 'action_btn', 'error_management'].includes(columnId);

  const isTodayStatus = status === 'accepted';

  const columns = useMemo(
    () => [
      {
        Header: 'Patient',
        accessor: (field: any) => {
          return (
            <div className="flex gap-x-0.8 items-center mr-[20px]">
              {field.patient ? (
                <>
                  <div className="words-break">{field.patient.name}</div>
                </>
              ) : (
                <div />
              )}
            </div>
          );
        },
        id: 'patientName',
      },
      {
        Header: 'Practitioner',
        accessor: (field: any) => {
          return <PractitionerNameColumn field={field} />;
        },
        id: 'doctorName',
      },
      {
        Header: 'Service',
        accessor: (field: any) => {
          const isServiceAvailable =
            !field.service.deletedAt &&
            field.service.isActive &&
            field.service.isAvailable;

          return (
            <div className="words-break pr-5">
              {field.service.name}
              {!isServiceAvailable && (
                <>
                  <RemovedInfo
                    className="inline-block ml-0.6"
                    data-for={field.service.id}
                    data-tip=""
                  />
                  <ReactTooltip
                    id={field.service.id}
                    place="bottom"
                    effect="solid"
                    className={`${styles.tooltip} shadow-tooltip`}
                  >
                    <span className="text-11 text-darkest-grey flex h-full items-center normal-case">
                      This service has been deactivated or removed
                    </span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
        id: 'serviceName',
        width: 250,
      },
      {
        Header: 'Appointment Date & Time',
        accessor: (field: any) => {
          return <AppointmentDateTimeColumn data={field} />;
        },
        id: 'appointmentDate',
        width: 180,
      },
      {
        Header: 'Requested Time',
        accessor: (field: any) => {
          const createdAtByTz = moment(field.createdAt).tz(field.timezone);

          return (
            <span>
              {createdAtByTz.format('MMM DD, YYYY')}
              <br />
              {createdAtByTz.format('hh:mm A Z UTC')}
            </span>
          );
        },
        id: 'createdAt',
      },
      {
        id: 'error_management',
        width: 72,
        Cell: ({ row }: any) => {
          const errorReason = getErrorReason(row.original);
          const awaitingList = (row.original as any)
            .awaitingAppointmentsByPatient;

          return (
            <div className="flex gap-x-1.6">
              <ErrorIcon appointment={{ ...row.original, errorReason }}>
                <PopoverOption
                  appointment={row.original}
                  isIconOptionDisabled={isTodayStatus}
                  updateDashboardUI={updateDashboardUI}
                />
              </ErrorIcon>
              {!!awaitingList?.length && (
                <ErrorIcon
                  appointment={{
                    ...row.original,
                    id: `${row.original.id}_rebooking`,
                    errorReason: ERROR_REASON.EXISTING_BOOKING_BY_PATIENT,
                    rebookingRecognitionDays:
                      awaitingList[0]?.rebookingRecognitionDays,
                  }}
                >
                  <RebookingPopover
                    appointments={awaitingList}
                    updateDashboardUI={updateDashboardUI}
                  />
                </ErrorIcon>
              )}
            </div>
          );
        },
      },
      {
        id: 'action_btn',
        width: 25,
        Header: ({ selectedFlatRows }: any) => {
          return (
            <Popover className="relative">
              <Popover.Button
                disabled={selectedFlatRows.length === 0}
                className="flex items-center disabled:cursor-not-allowed disabled:opacity-30"
              >
                <MoreIcon className="h-2.2" />
              </Popover.Button>

              <Popover.Panel className="flex flex-col gap-y-1.6 absolute py-2 right-0 z-10 w-16.5 bg-white shadow-primary rounded-[0.8rem] mt-0.6">
                {headerOptions.map((title) => (
                  <Popover.Button
                    key={title}
                    className="w-full px-2.5 hover:bg-opacity-10 hover:text-magenta text-left text-14 leaing-[2.1rem] text-darkest-grey"
                    onClick={() => {
                      setIsModalOpen(true);
                      setIsModalAccepted(title === 'Accept' ? true : false);
                      setIsAllSelected(true);
                    }}
                  >
                    {title}
                  </Popover.Button>
                ))}
              </Popover.Panel>
            </Popover>
          );
        },
        Cell: ({ row }: any) => {
          const handleClickOption = async (
            id: string,
            isModalAccepted: boolean
          ) => {
            setIsModalOpen(true);
            setIsRetrySelected(false);
            setIsModalAccepted(isModalAccepted);
            setChosenAppointmentId(id);
            await updateDashboardUI();
          };

          const handleRetryOption = async (id: string) => {
            setIsModalOpen(true);
            setIsRetrySelected(true);
            setChosenAppointmentId(id);
            await updateDashboardUI();
          };

          const onClickKeepBothAppts = async (
            appointment: IAppointmentDetail
          ) => {
            setSelectedApptForKeepBothModal(appointment);
            setIsKeepBothApptModalOpen(true);
          };

          const appointment = row.original;

          const isWarningIconEnabled = getIsWarningIconEnabled(appointment);

          const isExistedAfter24Hour =
            isWarningIconExistedAfter24Hour(appointment);

          const isThreeDotsOptionClickable = isWarningIconEnabled
            ? isExistedAfter24Hour
            : true;

          if (status === 'accepted' || !isThreeDotsOptionClickable) return null;

          return (
            <ThreeDotsOption
              key={highlightedRowId}
              appointment={appointment}
              options={options}
              onClickReject={() => handleClickOption(appointment.id, false)}
              onClickAccept={() => handleClickOption(appointment.id, true)}
              onClickKeepBothAppts={() => onClickKeepBothAppts(appointment)}
              onClickRetry={() => handleRetryOption(appointment.id)}
              updateDashboardUI={updateDashboardUI}
            />
          );
        },
      },
    ],
    [
      headerOptions,
      highlightedRowId,
      isTodayStatus,
      options,
      status,
      updateDashboardUI,
    ]
  );

  const dataTable = useMemo(() => {
    if (isLoading) {
      return { data: [], metadata: null };
    }
    return { data: data.data, metadata: data.metadata };
  }, [data?.data, data?.metadata, isLoading]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: dataTable.data ? dataTable.data : [],
      manualPagination: true,
      pageCount: dataTable.metadata ? data.metadata.total : 0,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    useFlexLayout,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          width: 50,
          Header: ({ rows, toggleRowSelected, selectedFlatRows }) => {
            const style = {
              cursor: 'pointer',
            };

            const numberOfNonErrorRows = rows.reduce((previous, current) => {
              const appointment = current.original as any;
              const isWarningIconEnabled = getIsWarningIconEnabled(appointment);
              const errorReason = getErrorReason(appointment);
              const isErrorIconDisplayed = isWarningIconEnabled || errorReason;

              if (!isErrorIconDisplayed) {
                return previous + 1;
              }
              return previous;
            }, 0);

            const checked: boolean =
              rows.length > 1 &&
              numberOfNonErrorRows > 0 &&
              selectedFlatRows.length === numberOfNonErrorRows;

            const indeterminate: boolean =
              !checked && selectedFlatRows.length > 0;

            const disabled: boolean =
              rows.length === 1 || status === 'accepted';

            const overiddenOnChange = (
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              rows.forEach((row) => {
                const appointment = row.original as any;
                const isWarningIconEnabled =
                  getIsWarningIconEnabled(appointment);
                const errorReason = getErrorReason(appointment);
                const isErrorIconDisplayed =
                  isWarningIconEnabled || errorReason;

                if (!isErrorIconDisplayed) {
                  toggleRowSelected(row.id, event.currentTarget.checked);
                }
              });
            };

            const modifiedToggleAllRowsProps = {
              onChange: overiddenOnChange,
              style: style,
              checked: checked,
              indeterminate: indeterminate,
              isDisabled: disabled,
            };

            return (
              <div className="flex items-center pl-1.7">
                <IndeterminateCheckbox {...modifiedToggleAllRowsProps} />
              </div>
            );
          },
          Cell: ({ row }) => {
            const appointment = row.original as any;
            const errorReason = !!getErrorReason(appointment);
            const isWarningIconEnabled = getIsWarningIconEnabled(appointment);

            return (
              <div className="flex items-center pl-1.7">
                <IndeterminateCheckbox
                  isDisabled={
                    status === 'accepted' || errorReason || isWarningIconEnabled
                  }
                  {...row.getToggleRowSelectedProps()}
                />
              </div>
            );
          },
        },
        ...columns,
      ]);
    }
  );

  const handlePaginate = (paginate: ITablePaginate) => {
    setCurrentPage(paginate.currPage);
    setPageSize(paginate.perPage);
  };

  const handleSortColumn = (column: HeaderGroup<object>) => {
    if (isActionColumn(column.id)) return;
    setCurrentPage(1);
    setSortBy(column.id);
    if (column.id === sortBy) {
      return setSort((sort) => (sort === 'asc' ? 'desc' : 'asc'));
    }
    setSort('asc');
  };

  const handleRebookRequest = async () => {
    try {
      await axiosInstance.patch(`/bookings/retry/${chosenAppointmentId}`);
      await updateDashboardUI();
      renderToast({
        type: 'success',
        message: `Successfully rebook appointment`,
      });
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        renderToast({
          type: 'error',
          message: 'Something went wrong. Please try again later',
        });

        return;
      }

      renderToast({
        type: 'error',
        message: error.message,
      });
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleConfirmRequest = async (isConfirmed: boolean) => {
    if (isConfirmed) {
      const updatedStatus = isModalAccepted ? 'accepted' : 'cancelled';

      const toastMessage = isModalAccepted ? 'accepted' : 'rejected';

      const body = isAllSelected
        ? selectedFlatRows.map((row: any) => ({
            appointmentId: row.original.id,
            status: updatedStatus,
          }))
        : [
            {
              appointmentId: chosenAppointmentId,
              status: updatedStatus,
            },
          ];

      try {
        await axiosInstance.patch('/appointments', body);
        await updateDashboardUI();
        renderToast({
          type: 'success',
          message: `Successfully ${toastMessage} appointment`,
        });
      } catch (error) {
        renderToast({
          type: 'error',
          message: 'Something went wrong. Please try again later',
        });
      }
    }
    setIsModalOpen(false);
    setIsAllSelected(false);
  };

  const displayTableBody = () => {
    if (isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[37rem] flex justify-center items-center">
                <LoadingSpinner className="all-child:fill-magenta" />
              </div>
            </td>
          </tr>
        </tbody>
      );
    }
    if (data.metadata.total === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={6}>
              <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                <EmptyBucket />
                <span>No appointment available on this list</span>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody {...getTableBodyProps()} className="text-darkest-grey">
        {page.map((row) => {
          prepareRow(row);
          const awaitingList = (row.original as any)
            .awaitingAppointmentsByPatient;

          return (
            <tr
              onMouseEnter={() => setHighlighedRowId(row.id)}
              {...row.getRowProps()}
              className={`bg-white border-b border-light-grey px-2 group hover:bg-lightPink items-center cursor-pointer relative`}
              style={{
                ...(row.getRowProps().style || {}),
                minHeight: `calc(12rem + ${awaitingList.length * 50}px)`,
              }}
            >
              {row.cells.map((cell: any) => {
                return (
                  <td
                    className={`text-left p-0 py-1.9 ${
                      awaitingList?.length ? 'self-start pt-1.9' : 'my-auto'
                    } ${
                      !isActionColumn(cell.column.id) &&
                      !awaitingList?.length &&
                      'cursor-pointer'
                    }`}
                    {...cell.getCellProps()}
                    style={{
                      ...(cell.getCellProps().style || {}),
                      minHeight: `calc(12rem + ${awaitingList.length * 50}px)`,
                    }}
                    onClick={() => {
                      if (!isActionColumn(cell.column.id)) {
                        setSelectedAppointmentId(cell.row.original.id);
                      }
                    }}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
              {awaitingList?.length > 0 && (
                <div className="absolute bottom-0.5 left-[9%] right-[9%] max-w-[80%] z-[1] flex flex-col gap-y-1">
                  {awaitingList.map((child: any) => (
                    <RebookingWarningMessage
                      key={`msg-${child.id}`}
                      appointment={child}
                    />
                  ))}
                </div>
              )}
            </tr>
          );
        })}
      </tbody>
    );
  };

  const renderModal = () => {
    if (isRetrySelected) {
      return (
        <ConfirmationModal
          title={'Rebook appointment request'}
          description={`Please ensure no appointment already exists in the PMS. To proceed, click the "Yes, retry" button. Alternatively, you can review and take action on each appointment individually.`}
          onClose={onModalClose}
          onSubmit={async () => await handleRebookRequest()}
          submitBtnTitle="Yes, retry"
        />
      );
    }

    if (isModalAccepted) {
      if (isAllSelected) {
        return (
          <ConfirmationModal
            title={'Accepting multiple appointments'}
            description={`You are in the process of accepting multiple appointments. To proceed, click the "Yes, accept" button. Alternatively, you can review and take action on each appointment individually.`}
            onClose={onModalClose}
            onSubmit={async () => await handleConfirmRequest(true)}
            submitBtnTitle="Yes, accept"
          />
        );
      } else {
        return (
          <AcceptAppointmentModal
            appointmentId={chosenAppointmentId}
            onClose={onModalClose}
          />
        );
      }
    }

    return (
      <RejectAppointmentModal
        appointmentId={chosenAppointmentId}
        onClose={onModalClose}
      />
    );
  };

  return (
    <>
      {isModalOpen ? renderModal() : null}
      {selectedAppointmentId && (
        <AppointmentDetailModal
          appointmentId={selectedAppointmentId}
          onClose={() => setSelectedAppointmentId('')}
          isHiddenAcceptRejectOptions={isTodayStatus}
        />
      )}
      {isKeepBothApptModalOpen && selectedApptForKeepBothModal && (
        <KeepBothAppointmentsModal
          appointment={selectedApptForKeepBothModal}
          awaitingAppointments={
            selectedApptForKeepBothModal.awaitingAppointmentsByPatient || []
          }
          onClose={() => {
            setIsKeepBothApptModalOpen(false);
            setSelectedApptForKeepBothModal(null);
          }}
        />
      )}
      <table {...getTableProps()} className="w-full text-14 leading-[2.1rem]">
        <thead className="font-bold">
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="px-2 items-center"
            >
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    {...column.getHeaderProps()}
                    className="text-left p-0 py-1.9"
                  >
                    <div
                      className="flex items-center gap-x-0.9 cursor-pointer"
                      onClick={() => handleSortColumn(column)}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                        {column.render('Header')}
                      </span>
                      {!isActionColumn(column.id) && (
                        <span>
                          <SortArrow
                            className={`${
                              column.id === sortBy && 'fill-magenta'
                            } ${
                              column.id === sortBy &&
                              sort === 'asc' &&
                              'rotate-180'
                            } transition-all`}
                          />
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {displayTableBody()}
      </table>
      {dataTable?.metadata && dataTable.metadata.total > 0 && (
        <div className="py-1.6">
          <Pagination
            key={status}
            isBorderNotIncluded
            handlePaginating={handlePaginate}
            data={{
              currPage: data.metadata.page,
              perPage: data.metadata.limit,
              total: data.metadata.total,
            }}
          />
        </div>
      )}
    </>
  );
};

export default Table;
